/**
 * 이상한 방 - 문구 엔진.
 *
 * 라이브러리를 쓰지 않는다. 문구가 수백 개 수준이고, 이 게임이 문구에 요구하는 것은
 * 딱 두 가지뿐이라 그 둘만 정확히 하면 된다.
 *
 * 1. **어순을 언어에 맡긴다.** `'거실의 ' + name + '이 사라졌다'` 같은 이어붙이기를 하면
 *    영어에서 문장이 뒤집히는 순간 손쓸 방법이 없다. 그래서 전부 템플릿이다.
 * 2. **한국어 조사를 맞춘다.** "소파가"와 "머그컵이"를 못 가르면 그 화면은 기계 번역체가 되고,
 *    클립에 쓸 수 있는 화면이 아니게 된다. 리빌은 이 게임의 클라이맥스라 여기서 타협하지 않는다.
 *
 * ## 템플릿 문법
 *
 * | 표기                  | 뜻                                        |
 * |-----------------------|-------------------------------------------|
 * | `{item}`              | 값을 그대로 넣는다                          |
 * | `{item:이/가}`        | 값 + 받침에 맞는 한국어 조사                 |
 * | `{count|one|other}`   | 값이 1이면 `one`, 아니면 `other` (영어 복수) |
 *
 * 조사 짝은 **받침 있는 쪽을 앞에** 적는다(`이/가`, `은/는`, `과/와`). 표기가 흔들리면
 * 사전마다 다른 규칙을 외워야 하므로 규칙은 하나다.
 *
 * ## 없는 키
 *
 * 개발 모드에서는 화면에 `[[key]]`가 뜨고 콘솔에 한 번 에러가 난다. 키 문자열을 조용히
 * 그대로 출력하면 "그런 라벨인가 보다" 하고 지나가고, 그렇게 남은 구멍이 배포까지 간다.
 */

import { en } from './en'
import { ko, type MessageKey, type Messages } from './ko'

export type { MessageKey, Messages }

/* --- 로케일 --------------------------------------------------------------------- */

export const LOCALES = ['ko', 'en'] as const

export type Locale = (typeof LOCALES)[number]

/** 치환 값. 숫자를 그대로 받는 이유는 호출부마다 `String()`을 부르게 하지 않기 위해서다. */
export type Params = Readonly<Record<string, string | number>>

/**
 * 값이 비어 있을 때 돌아갈 언어이자 키의 원본. 한국어 사전이 언제나 완전하다는 뜻이며,
 * 새 문구는 여기부터 들어온다.
 */
const FALLBACK_LOCALE: Locale = 'ko'

const DICTIONARIES: Readonly<Record<Locale, Messages>> = { ko, en }

/** localStorage 키. 다른 저장값과 섞이지 않게 게임 이름을 앞에 둔다. */
const STORAGE_KEY = 'weird-room.locale'

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value)
}

/* --- 저장소 --------------------------------------------------------------------- */

/*
 * localStorage는 있는 척하면서 던지는 환경이 실제로 있다(사파리 프라이빗, 쿠키 차단,
 * iframe 안의 서드파티 컨텍스트). 언어 하나 못 읽었다고 게임이 안 켜지면 안 되므로
 * 읽기와 쓰기 모두 조용히 실패한다. 언어는 다시 고르면 그만인 값이다.
 */

function readStoredLocale(): Locale | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return isLocale(raw) ? raw : null
  } catch {
    return null
  }
}

function writeStoredLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // 저장하지 못해도 이번 세션의 선택은 살아 있다. 다음 방문에 다시 고르면 된다.
  }
}

/* --- 감지 ---------------------------------------------------------------------- */

/** 브라우저가 말하는 선호 언어 목록. `languages`가 없는 구형 WebView는 `language` 하나뿐이다. */
function browserLanguages(): readonly string[] {
  /*
   * `navigator`가 있는지가 아니라 **브라우저인지**를 묻는다.
   *
   * Node 21부터 `navigator`가 전역으로 들어왔고 `navigator.languages`도 있다. 그래서
   * `typeof navigator === 'undefined'` 하나로는 브라우저와 Node를 못 가른다. 지금 Node가
   * 돌려주는 값은 브라우저 설정이 아니라 **그 기계의 OS 로케일**이고, 그 결과가 이렇다.
   *
   *  - 테스트가 `environment: 'node'`에서 돌면 문구가 실행 기계의 언어로 나온다.
   *    영어 로케일 CI에서는 영어, 한국어 노트북에서는 한국어 - 같은 코드가 다른 문구를 낸다.
   *    이 프로젝트에서 결정론은 전제이고(시드), 화면 문구도 예외가 아니다.
   *  - 서버에서 문구를 만들 일이 생기면 기계 설정이 조용히 언어를 정한다.
   *
   * `window`가 있어야 브라우저다. jsdom에는 `window`가 있으므로 브라우저 언어를 보는
   * 테스트(`index.test.ts`의 `stubLanguages`)는 그대로 동작한다. Node에서는 단서가 없는 것으로
   * 보고 `FALLBACK_LOCALE`로 떨어진다 - 사전이 완전한 쪽이라 그게 옳은 기본값이다.
   */
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return []

  const list = navigator.languages
  if (Array.isArray(list) && list.length > 0) return list
  return typeof navigator.language === 'string' ? [navigator.language] : []
}

/**
 * 지금 이 사람에게 보여 줄 언어. 저장된 선택이 언제나 이긴다.
 *
 * 브라우저 언어를 저장값보다 앞세우면, 한국어를 고른 사람이 영어 로케일 폰을 쓸 때
 * 새로고침마다 언어가 되돌아간다. 고른 것은 고른 것이다.
 */
export function detectLocale(): Locale {
  const stored = readStoredLocale()
  if (stored !== null) return stored

  for (const tag of browserLanguages()) {
    const base = tag.toLowerCase().split('-')[0]
    if (isLocale(base)) return base
  }

  // 아무 단서가 없으면 원본 언어로 간다. 사전이 완전한 쪽이라 최소한 빈 화면은 나오지 않는다.
  return FALLBACK_LOCALE
}

/* --- 상태 ---------------------------------------------------------------------- */

let currentLocale: Locale = detectLocale()

type LocaleListener = (locale: Locale) => void

const listeners = new Set<LocaleListener>()

export function getLocale(): Locale {
  return currentLocale
}

/**
 * 언어를 바꾸고 저장한다. 같은 값이면 아무 일도 하지 않는다.
 *
 * 화면 갱신은 이 함수의 일이 아니다. HUD·결과·선택 화면이 각자 다시 그리는 시점을 알고 있고,
 * 여기서 DOM을 건드리기 시작하면 문구 모듈이 화면 구조를 알아야 한다.
 */
export function setLocale(locale: Locale): void {
  if (!isLocale(locale) || locale === currentLocale) return
  currentLocale = locale
  writeStoredLocale(locale)
  for (const listener of listeners) listener(locale)
}

/** 언어가 바뀌면 다시 그려야 하는 화면이 구독한다. 돌려주는 함수를 부르면 구독이 끊긴다. */
export function onLocaleChange(listener: LocaleListener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/* --- 한글 조사 ------------------------------------------------------------------ */

const HANGUL_FIRST = 0xac00
const HANGUL_LAST = 0xd7a3
/** 한글 음절 = 초성 x 중성 x 종성(28). 나머지가 종성 인덱스이고 0이면 받침이 없다. */
const JONGSEONG_COUNT = 28
/** 종성 ㄹ. `으로/로`만 이 받침을 "없는 것"처럼 다룬다("서울로", "칼로"). */
const JONGSEONG_RIEUL = 8

/**
 * 숫자로 끝나는 이름의 종성. 눈에 보이는 글자가 아니라 **읽는 소리**로 판정한다.
 * "의자 1이" 가 아니라 "의자 1(일)이", "탄환 2(이)가" 여야 한다.
 */
const DIGIT_JONGSEONG: Readonly<Record<string, number>> = {
  '0': 21, // 영
  '1': JONGSEONG_RIEUL, // 일
  '2': 0, // 이
  '3': 16, // 삼
  '4': 0, // 사
  '5': 0, // 오
  '6': 1, // 육
  '7': JONGSEONG_RIEUL, // 칠
  '8': JONGSEONG_RIEUL, // 팔
  '9': 0, // 구
}

/** 화면에서 실제로 쓰는 조사 짝. 받침 있는 쪽이 앞이다. */
export type JosaPair =
  | '이/가'
  | '은/는'
  | '을/를'
  | '과/와'
  | '으로/로'
  | '이나/나'
  | '이라/라'
  | '이랑/랑'
  | '아/야'

const KNOWN_PAIRS: ReadonlySet<string> = new Set<JosaPair>([
  '이/가',
  '은/는',
  '을/를',
  '과/와',
  '으로/로',
  '이나/나',
  '이라/라',
  '이랑/랑',
  '아/야',
])

/**
 * 마지막 글자의 종성 인덱스. 판정할 수 없으면 `null`.
 *
 * 라틴 문자로 끝나는 이름("브라운관 TV")은 읽는 사람마다 소리가 갈려 규칙이 서지 않는다.
 * 그럴 때는 받침 없는 기본형을 쓰는 편이 덜 틀린다.
 */
function jongseongOf(word: string): number | null {
  const last = word.trim().slice(-1)
  if (last === '') return null

  const digit = DIGIT_JONGSEONG[last]
  if (digit !== undefined) return digit

  const code = last.charCodeAt(0)
  if (code < HANGUL_FIRST || code > HANGUL_LAST) return null
  return (code - HANGUL_FIRST) % JONGSEONG_COUNT
}

/** 마지막 글자에 받침이 있는가. 판정 불가는 없는 것으로 본다. */
export function hasFinalConsonant(word: string): boolean {
  const jong = jongseongOf(word)
  return jong !== null && jong !== 0
}

/** 조사만 고른다. 단어와 붙이지 않고 필요할 때가 있어 따로 열어 둔다. */
export function particleFor(word: string, pair: JosaPair | string): string {
  const slash = pair.indexOf('/')
  if (slash < 0) return ''

  const withBatchim = pair.slice(0, slash)
  const withoutBatchim = pair.slice(slash + 1)

  const jong = jongseongOf(word)
  if (jong === null || jong === 0) return withoutBatchim

  // `으로/로`는 ㄹ 받침을 받침 없는 것처럼 다룬다. 짝의 앞이 '으'로 시작하는지로 가른다.
  if (withBatchim.startsWith('으') && jong === JONGSEONG_RIEUL) return withoutBatchim

  return withBatchim
}

/** 단어에 알맞은 조사를 붙여 돌려준다. `josa('소파', '이/가')` → `'소파가'`. */
export function josa(word: string, pair: JosaPair): string {
  return word + particleFor(word, pair)
}

/* --- 없는 키 ------------------------------------------------------------------- */

/** 이미 알린 키. HUD는 프레임마다 그려지므로 같은 경고를 60번씩 찍으면 콘솔이 죽는다. */
const reportedKeys = new Set<string>()

function isDevMode(): boolean {
  try {
    return import.meta.env.DEV === true
  } catch {
    return false
  }
}

function reportOnce(message: string, tag: string): void {
  if (!isDevMode() || reportedKeys.has(tag)) return
  reportedKeys.add(tag)
  console.error(`[i18n] ${message}`)
}

/** 개발 중 놓친 키를 화면 밖에서도 확인할 수 있게 열어 둔다(테스트와 디버그 오버레이용). */
export function reportedProblems(): readonly string[] {
  return [...reportedKeys]
}

/** 테스트가 같은 키를 다시 관찰할 수 있게 기록을 비운다. */
export function resetReportedProblems(): void {
  reportedKeys.clear()
}

/** 조립한 키가 사전에 실제로 있는지. 없는 구획 id 같은 것을 화면에 흘리기 전에 거른다. */
export function hasKey(key: string): key is MessageKey {
  return Object.hasOwn(DICTIONARIES[FALLBACK_LOCALE], key)
}

/* --- 치환 ---------------------------------------------------------------------- */

/** `{...}` 하나. 중괄호를 다시 열지 않으므로 중첩은 없다. */
const PLACEHOLDER = /\{([^{}]+)\}/g

/** 복수형 표기 `{n|one|other}`에서 1을 가르는 기준. 0과 음수는 복수로 읽는다. */
const SINGULAR = 1

function fill(template: string, params: Params | undefined, key: string): string {
  return template.replace(PLACEHOLDER, (whole: string, token: string): string => {
    const plural = token.split('|')
    if (plural.length === 3) {
      const [name, one, other] = plural
      if (name === undefined || one === undefined || other === undefined) return whole
      const value = params?.[name]
      if (value === undefined) return missingParam(whole, name, key)
      return Number(value) === SINGULAR ? one : other
    }

    const colon = token.indexOf(':')
    if (colon < 0) {
      const value = params?.[token]
      if (value === undefined) return missingParam(whole, token, key)
      return String(value)
    }

    const name = token.slice(0, colon)
    const pair = token.slice(colon + 1)
    const value = params?.[name]
    if (value === undefined) return missingParam(whole, name, key)
    if (!KNOWN_PAIRS.has(pair)) {
      reportOnce(`'${key}'의 조사 짝 '${pair}'을 모른다. 받침 있는 쪽을 앞에 적어라.`, `pair:${pair}`)
      return String(value)
    }

    const word = String(value)
    return word + particleFor(word, pair)
  })
}

function missingParam(whole: string, name: string, key: string): string {
  reportOnce(`'${key}'에 넘길 값 '${name}'이 없다.`, `param:${key}:${name}`)
  return whole
}

/* --- 조회 ---------------------------------------------------------------------- */

/**
 * 사전에서 문구 하나를 꺼낸다. 자기 속성만 본다.
 *
 * 키가 런타임에 조립되므로(`'room.' + zone.id`) 언젠가 `constructor`나 `toString`이
 * 키 자리에 올 수 있다. 그냥 인덱싱하면 Object.prototype의 함수가 문구인 척 돌아오고,
 * 그 다음 줄에서 함수에 `.replace`를 부르며 죽는다.
 */
function lookup(locale: Locale, key: string): string | undefined {
  const dictionary: Readonly<Record<string, string>> = DICTIONARIES[locale]
  return Object.hasOwn(dictionary, key) ? dictionary[key] : undefined
}

/**
 * 키에 해당하는 문구를 지금 언어로 돌려준다.
 *
 * 키 이름은 자동완성으로 뜨지만 문자열도 받는다. `t('room.' + zone.id)`처럼 런타임에
 * 조립하는 자리가 실제로 있고(구획 이름, 동사, 거절 사유), 그때마다 단언을 쓰게 하면
 * 오히려 타입이 거짓말을 하기 시작한다.
 */
export function t(key: MessageKey | (string & {}), params?: Params): string {
  const template = lookup(currentLocale, key)
  if (template !== undefined) return fill(template, params, key)

  const fallback = lookup(FALLBACK_LOCALE, key)
  if (fallback !== undefined) {
    reportOnce(
      `'${key}'가 ${currentLocale} 사전에 없다. ${FALLBACK_LOCALE}로 대신 보여 준다.`,
      `fallback:${currentLocale}:${key}`,
    )
    return fill(fallback, params, key)
  }

  // 어느 사전에도 없다. 개발 중에는 눈에 띄어야 하고, 배포판에서는 화면이 깨지지 않아야 한다.
  reportOnce(`'${key}'는 어느 사전에도 없다.`, `missing:${key}`)
  return isDevMode() ? `[[${key}]]` : key
}
